import React from "react";
import { useNavigate } from "react-router-dom";
import RefreshIcon from "@mui/icons-material/Refresh";
import InsightsIcon from "@mui/icons-material/Insights";
import { CardSkeleton } from "../../components/skeleton/ListSkeleton";
import { CARD } from "../analytics/components/ui";
import { useDashboardToday } from "./useDashboardToday";
import { formatClock, headerDate, visibleTodoBlocks } from "./dashboardTodayUtils";
import { isBookMyDentistOnly, isTreatmentsEnabled } from "../../utils/clinicFeatures";
import {
  AccountAlerts,
  LabDue,
  LowStock,
  NextUp,
  OnlineToResolve,
  PerDentist,
  TodayStrip,
  TomorrowList,
  TreatedNotBilled,
  UnfinishedPlans,
  WaitingRoom,
} from "./components/TodayBlocks";

/**
 * Today page: what needs the desk right now at the top, totals lower, trends
 * left to Analytics. No money amounts here; those live in Payments and Analytics. Every block reads one payload
 * (GET client/dashboard/today) and hides itself when it has nothing to show.
 */
function Dashboard() {
  const navigate = useNavigate();
  const bookMyDentistOnly = isBookMyDentistOnly();
  const { data, loading, error, refresh } = useDashboardToday();

  if (loading && !data) {
    return (
      <div className="space-y-4">
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
          <CardSkeleton cards={6} height="h-20" />
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div className="lg:col-span-2">
            <CardSkeleton cards={1} height="h-64" />
          </div>
          <CardSkeleton cards={1} height="h-64" />
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className={`${CARD} p-10 text-center`}>
        <p className="text-sm font-semibold text-slate-700 dark:text-slate-200">The dashboard could not be loaded.</p>
        <button type="button" onClick={refresh} className="mt-3 text-sm font-medium text-primary hover:underline">
          Try again
        </button>
      </div>
    );
  }

  const show = visibleTodoBlocks(data, { treatments: isTreatmentsEnabled() });
  const updated = new Date(data.generatedAt);
  const updatedLabel = Number.isNaN(updated.getTime())
    ? ""
    : formatClock(`${String(updated.getHours()).padStart(2, "0")}:${String(updated.getMinutes()).padStart(2, "0")}`);

  return (
    <div className="space-y-5 text-slate-800 dark:text-slate-200">
      <div className="flex flex-wrap items-end justify-between gap-2">
        <div>
          <h1 className="text-xl font-bold text-slate-800 dark:text-slate-100">Today · {headerDate(data.date)}</h1>
          <p className="text-xs text-slate-500 dark:text-slate-400">
            {error ? "Showing the last numbers — refresh failed" : `Updated ${updatedLabel} · refreshes every minute`}
          </p>
        </div>
        <button
          type="button"
          onClick={refresh}
          className="inline-flex items-center gap-1 text-xs font-medium text-slate-500 hover:text-primary dark:text-slate-400"
        >
          <RefreshIcon sx={{ fontSize: 16 }} /> Refresh
        </button>
      </div>

      <TodayStrip counts={data.counts} />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2">
          <NextUp rows={data.nextUp} />
        </div>
        <WaitingRoom rows={data.waiting} longWaitMinutes={data.longWaitMinutes} />
      </div>

      <PerDentist rows={data.dentists} />

      {data.owner && <AccountAlerts owner={data.owner} />}

      {(show.treatedNotBilled || show.onlineToResolve || show.labDue || show.lowStock || show.unfinishedPlans || data.tomorrowList.count > 0) && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 items-start">
          {show.treatedNotBilled && <TreatedNotBilled rows={data.treatedNotBilled} />}
          {show.onlineToResolve && <OnlineToResolve data={data.onlineToResolve} />}
          <TomorrowList list={data.tomorrowList} />
          {show.labDue && <LabDue rows={data.labDue} />}
          {show.unfinishedPlans && <UnfinishedPlans rows={data.unfinishedPlans} />}
          {show.lowStock && <LowStock data={data.lowStock} />}
        </div>
      )}

      <button
        type="button"
        onClick={() => navigate(bookMyDentistOnly ? "/client/analytics/bookmydentist" : "/client/analytics/overview")}
        className={`${CARD} w-full px-4 py-3 flex items-center justify-center gap-2 text-sm text-slate-600 hover:text-primary dark:text-slate-300`}
      >
        <InsightsIcon sx={{ fontSize: 18 }} />
        {bookMyDentistOnly
          ? "Online bookings and appointment trends are in Analytics"
          : "Trends, treatment mix and monthly reports are in Analytics"}
      </button>
    </div>
  );
}

export default Dashboard;
